import { keepPreviousData } from '@tanstack/react-query';

import { useAxios } from '@/hooks/useAxios';
import { useGetUserKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';

interface TypeCost {
  cost: number;
  duration_seconds: number;
  job_count?: number;
}

interface CapabilityCost {
  duration_seconds: number;
  job_count: number;
}

interface SubscriptionInfo {
  tier?: string;
  intelligence_tier?: string;
  msrp?: number;
  subscribed_assets?: number;
  subscribed_applications?: number;
  contract_start?: string;
  contract_end?: string;
}

export interface CostWindow {
  period_days: number;
  date?: string;
  scan_cost: number;
  ai_cost: number;
  total_cost: number;
  annual_scan_cost: number;
  annual_ai_cost: number;
  annual_total_cost: number;
  by_entity_type?: Record<string, TypeCost>;
  by_capability?: Record<string, CapabilityCost>;
  scan_count: number;
  total_jobs: number;
  total_duration_seconds: number;
  ai_subscription_credit_monthly?: number;
  ai_budget_limit_monthly?: number;
  ai_budget_usage_monthly?: number;
  ai_sales_attributed: number;
  cogs_ai_cost: number;
  cogs_compute_cost: number;
  cogs_cost: number;
  annual_cogs_ai: number;
  annual_cogs_compute: number;
  annual_cogs: number;
  sales_ai: number;
  sales_compute: number;
  annual_sales_ai: number;
  annual_sales_compute: number;
  snapshot_customer_type?: string;
}

export interface TenantCostResponse {
  windows: CostWindow[];
  acv: number;
  estimated_cost?: number;
  scan_frequency: string;
  contracted_assets: number;
  actual_assets: number;
  cost_per_asset: number;
  gross_margin: number | null;
  subscription?: SubscriptionInfo;
  asset_counts?: Record<string, number>;
  risk_counts?: Record<string, number>;
  code_repo_counts?: Record<string, number>;
  customer_type?: string;
}

export function useGetAccountCost(options?: {
  email?: string;
  enabled?: boolean;
  from?: string;
  to?: string;
  series?: boolean;
}) {
  const axios = useAxios({ email: options?.email });
  const key = useGetUserKey(
    [
      'account',
      'cost',
      options?.from ?? '',
      options?.to ?? '',
      options?.series ? 'series' : '',
    ],
    false,
    options?.email
  );

  return useQuery<TenantCostResponse>({
    defaultErrorMessage: 'Failed to load cost data',
    queryKey: key,
    queryFn: () => {
      const params: Record<string, string> = {};
      if (options?.from) params.from = options.from;
      if (options?.to) params.to = options.to;
      if (options?.series) params.series = 'true';
      return axios.get('/account/cost', { params }).then(r => r.data);
    },
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 300_000,
    gcTime: 600_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useGetCostEstimate(options?: {
  email?: string;
  enabled?: boolean;
  from?: string;
  to?: string;
  acv?: number;
  aiCredit?: number;
  customerType?: string;
}) {
  const axios = useAxios({ email: options?.email });
  const key = useGetUserKey(
    [
      'account',
      'cost',
      'estimate',
      options?.from ?? '',
      options?.to ?? '',
      String(options?.acv ?? ''),
      String(options?.aiCredit ?? ''),
      options?.customerType ?? '',
    ],
    false,
    options?.email
  );

  const hasDateRange = Boolean(options?.from && options?.to);

  return useQuery<TenantCostResponse>({
    defaultErrorMessage: 'Failed to load cost estimate',
    queryKey: key,
    queryFn: () => {
      const params: Record<string, string> = {};
      if (options?.from) params.from = options.from;
      if (options?.to) params.to = options.to;
      if (options?.acv !== undefined) params.acv = String(options.acv);
      if (options?.aiCredit !== undefined)
        params.ai_credit = String(options.aiCredit);
      if (options?.customerType) params.customer_type = options.customerType;
      return axios.get('/account/cost/estimate', { params }).then(r => r.data);
    },
    enabled: (options?.enabled ?? true) && hasDateRange,
    placeholderData: keepPreviousData,
    staleTime: 300_000,
    gcTime: 600_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
